import React, { useState, useEffect, useRef } from 'react';
import { useTheme } from '@mui/material/styles';
import Box from '@mui/material/Box';
import Typography from '@mui/material/Typography';
import { SelectInput, TextInput } from 'components/Inputs';
import Button from 'components/Button';
import { get, post } from 'api';
import { SuccessToast, ErrorToast } from 'utils/Toasts';
import moment from 'moment';
import { DatePicker } from '@mui/x-date-pickers';
import SpinningBox from 'components/SpinningBox';
import { useTranslation } from 'react-i18next';
import { PadelLevels } from 'constants/Selections';

const NewTournament = ({ setCurrentView }) => {
  const theme = useTheme();
  const { t } = useTranslation();
  const successToastRef = useRef();
  const errorToastRef = useRef();
  const [open, setOpen] = useState({});
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [tournamentGenders, setTournamentGenders] = useState([]);
  const [form, setForm] = useState({
    city: '',
    tournamentName: '',
    tournamentType: '',
    earlyBirdCode: '',
    earlyBirdDate: '',
    registrationDate: '',
    tournamentStartDate: '',
    tournamentStartDay: '',
    finalsDate: '',
    finalDay: '',
    groupsDate: '',
    eliminationsDate: '',
    price: '',
    divisions: '',
    men_enabled: 0,
    menId: '',
    women_enabled: 0,
    womenId: '',
    mix_enabled: 0,
    mixId: '',
    menLevels: [],
    womenLevels: [],
    mixLevels: [],
    men_max_players: '',
    women_max_players: '',
    mix_max_players: '',
    collect_payment: 1,
    new_customer_price: '',
    isContinued: 1,
  });

  useEffect(() => {
    const fetchTypes = async () => {
      try {
        const response = await get(
          '/tournament-registration/tournament-genders',
        );
        const data = await response.json();
        setTournamentGenders(data);
      } catch (error) {
        console.error('Failed to fetch levels', error);
      }
    };

    fetchTypes();
  }, []);

  const handleOpen = (id) => {
    setOpen({ ...open, [id]: true });
  };

  const handleClose = (id) => {
    setOpen({ ...open, [id]: false });
  };

  const handleChange = (event) => {
    setForm({
      ...form,
      [event.target.name]: event.target.value,
    });
  };

  const handleSubmit = async () => {
    setIsSubmitting(true);
    const body = {
      city: form.city,
      tournament_name: form.tournamentName,
      tournament_type: form.tournamentType,
      early_bird: form.earlyBirdCode || null,
      early_bird_date: form.earlyBirdDate || null,
      registration_date: form.registrationDate,
      start_date: form.tournamentStartDate,
      start_day: form.tournamentStartDay,
      final_date: form.finalsDate,
      final_day: form.finalDay,
      groups_date: form.groupsDate || null,
      eliminations_date: form.eliminationsDate || null,
      price: form.price,
      divisions: form.divisions,
      men_enabled: form.men_enabled,
      men_id: parseInt(form.menId),
      menLevels: form.menLevels.join(','),
      women_enabled: form.women_enabled,
      women_id: parseInt(form.womenId),
      womenLevels: form.womenLevels.join(','),
      mix_enabled: form.mix_enabled,
      mix_id: parseInt(form.mixId),
      mixLevels: form.mixLevels.join(','),
      men_max_players: parseInt(form.men_max_players) || null,
      women_max_players: parseInt(form.women_max_players) || null,
      mix_max_players: parseInt(form.mix_max_players) || null,
      isContinued: form.isContinued,
      collect_payment: form.collect_payment,
      new_customer_price: form.new_customer_price,
    };
    const response = await post('/tournaments/newTournament', body);
    const data = await response.json();
    if (data.status === 'SUCCESS') {
      successToastRef.current.showSuccess(data.msg);
      setTimeout(() => {
        setIsSubmitting(false);
        setForm({
          city: '',
          tournamentName: '',
          tournamentType: '',
          earlyBirdCode: '',
          earlyBirdDate: '',
          registrationDate: '',
          tournamentStartDate: '',
          tournamentStartDay: '',
          finalsDate: '',
          finalDay: '',
          groupsDate: '',
          eliminationsDate: '',
          price: '',
          divisions: '',
          men: 0,
          menId: '',
          women: 0,
          womenId: '',
          mix: 0,
          mixId: '',
          menLevels: [],
          womenLevels: [],
          mixLevels: [],
          maxPlayers: null,
          isContinued: 1,
          collect_payment: 1,
          new_customer_price: '',
        });
        setCurrentView('list');
        window.scrollTo(0, 0);
      }, 2000);
    } else {
      errorToastRef.current.showError(data.err);
      setIsSubmitting(false);
    }
  };

  return (
    <Box
      sx={{
        maxWidth: { xs: '100%', sm: '100%', md: '516px' },
        width: '100%',
        height: 'auto',
        display: 'flex',
        flexDirection: 'column',
      }}
    >
      <SuccessToast ref={successToastRef} />
      <ErrorToast ref={errorToastRef} />
      <Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            {t('city')}:
          </Typography>
          <SelectInput
            name="city"
            id="city"
            placeholder={t('selectCity')}
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleChange}
            topMargin={'6px'}
            value={form.city || ''}
            options={[
              { value: 'Vilnius', label: 'Vilnius' },
              { value: 'Kaunas', label: 'Kaunas' },
              { value: 'Panevėžys', label: 'Panevėžys' },
            ]}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <TextInput
            label={t('tournamentName')}
            id="tournamentName"
            name="tournamentName"
            type="text"
            placeholder={t('tournamentName')}
            required
            handleInputChange={handleChange}
            value={form.tournamentName}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            {t('tournamentType')}:
          </Typography>
          <SelectInput
            name="tournamentType"
            id="tournamentType"
            placeholder={t('selectTournamentType')}
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleChange}
            topMargin={'6px'}
            value={form.tournamentType || ''}
            options={[
              { value: 'singles', label: t('singles') },
              { value: 'doubles', label: t('doubles') },
            ]}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            {t('continued')}:
          </Typography>
          <SelectInput
            name="isContinued"
            id="isContinued"
            placeholder={t('continued')}
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleChange}
            topMargin={'6px'}
            value={form.isContinued}
            options={[
              {
                value: 1,
                label: 'Taip',
              },
              {
                value: 0,
                label: 'Ne',
              },
            ]}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', sm: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            {t('gender')}:
          </Typography>
          <Box display="flex" flexDirection="column" marginTop="8px">
            {tournamentGenders.map((type) => (
              <Box
                key={type.id}
                display="flex"
                alignItems="center"
                marginBottom="8px"
              >
                <input
                  style={{
                    width: '16px',
                    height: '16px',
                    cursor: 'pointer',
                    margin: '0',
                    padding: '0',
                    border: 'none',
                    outline: 'none',
                    borderRadius: '4px',
                  }}
                  type="checkbox"
                  id={`gender_${type.id}`}
                  name={`${type.name}`}
                  value={type.id}
                  checked={form[`${type.name}_enabled`] === 1} // Check if enabled
                  onChange={(event) => {
                    const isChecked = event.target.checked;
                    const genderKey = `${type.name}_enabled`;
                    const genderIdKey = `${type.name}Id`;

                    setForm({
                      ...form,
                      [genderKey]: isChecked ? 1 : 0, // Enable or disable
                      [genderIdKey]: isChecked ? type.id : '', // Set or clear gender_id
                    });
                  }}
                />
                <label
                  htmlFor={`gender_${type.id}`}
                  style={{ marginLeft: '8px', cursor: 'pointer' }}
                >
                  <Typography
                    variant="body3"
                    fontWeight={500}
                    color={theme.palette.darkGrey[700]}
                  >
                    {t(`${type.name}`)}
                  </Typography>
                </label>
              </Box>
            ))}
          </Box>
        </Box>
        {form.men_enabled === 1 && (
          <>
            <Box
              display={'flex'}
              flexDirection={'column'}
              maxWidth={{ xs: '100%', sm: '100%' }}
              marginTop={'16px'}
            >
              <Typography
                variant="body4"
                fontWeight={500}
                color={theme.palette.darkGrey[700]}
              >
                {t('men')} {t('level')}:
              </Typography>
              <SelectInput
                name="level_id"
                id="level_id"
                placeholder={t('selectLevel')}
                open={open}
                handleOpen={handleOpen}
                handleClose={handleClose}
                handleChange={(event) => {
                  const selectedValues = Array.isArray(event.target.value)
                    ? event.target.value
                    : [event.target.value];
                  setForm({
                    ...form,
                    menLevels: selectedValues,
                  });
                }}
                topMargin={'6px'}
                value={form.menLevels || []}
                multiple
                options={PadelLevels.map((level) => ({
                  value: level,
                  label: level,
                }))}
              />
            </Box>
            {form.isContinued === 0 && (
              <Box
                display={'flex'}
                flexDirection={'column'}
                maxWidth={{ xs: '100%', md: '100%' }}
                marginTop={'24px'}
              >
                <TextInput
                  label={`${t('men')} ${t('maxPlayers')}`}
                  id="men_max_players"
                  name="men_max_players"
                  type="number"
                  placeholder={'Pvz.: 8'}
                  required
                  handleInputChange={handleChange}
                  value={form.men_max_players}
                />
              </Box>
            )}
          </>
        )}
        {form.women_enabled === 1 && (
          <>
            <Box
              display={'flex'}
              flexDirection={'column'}
              maxWidth={{ xs: '100%', sm: '100%' }}
              marginTop={'16px'}
            >
              <Typography
                variant="body4"
                fontWeight={500}
                color={theme.palette.darkGrey[700]}
              >
                {t('women')} {t('level')}:
              </Typography>
              <SelectInput
                name="level_id"
                id="level_id"
                placeholder={t('selectLevel')}
                open={open}
                handleOpen={handleOpen}
                handleClose={handleClose}
                handleChange={(event) => {
                  const selectedValues = Array.isArray(event.target.value)
                    ? event.target.value
                    : [event.target.value];
                  setForm({
                    ...form,
                    womenLevels: selectedValues,
                  });
                }}
                topMargin={'6px'}
                value={form.womenLevels || []}
                multiple
                options={PadelLevels.map((level) => ({
                  value: level,
                  label: level,
                }))}
              />
            </Box>
            {form.isContinued === 0 && (
              <Box
                display={'flex'}
                flexDirection={'column'}
                maxWidth={{ xs: '100%', md: '100%' }}
                marginTop={'24px'}
              >
                <TextInput
                  label={`${t('women')} ${t('maxPlayers')}`}
                  id="women_max_players"
                  name="women_max_players"
                  type="number"
                  placeholder={'Pvz.: 8'}
                  required
                  handleInputChange={handleChange}
                  value={form.women_max_players}
                />
              </Box>
            )}
          </>
        )}
        {form.mix_enabled === 1 && (
          <>
            <Box
              display={'flex'}
              flexDirection={'column'}
              maxWidth={{ xs: '100%', sm: '100%' }}
              marginTop={'16px'}
            >
              <Typography
                variant="body4"
                fontWeight={500}
                color={theme.palette.darkGrey[700]}
              >
                {t('mix')} {t('level')}:
              </Typography>
              <SelectInput
                name="level_id"
                id="level_id"
                placeholder={t('selectLevel')}
                open={open}
                handleOpen={handleOpen}
                handleClose={handleClose}
                handleChange={(event) => {
                  const selectedValues = Array.isArray(event.target.value)
                    ? event.target.value
                    : [event.target.value];
                  setForm({
                    ...form,
                    mixLevels: selectedValues,
                  });
                }}
                topMargin={'6px'}
                value={form.mixLevels || []}
                multiple
                options={PadelLevels.map((level) => ({
                  value: level,
                  label: level,
                }))}
              />
            </Box>
            {form.isContinued === 0 && (
              <Box
                display={'flex'}
                flexDirection={'column'}
                maxWidth={{ xs: '100%', md: '100%' }}
                marginTop={'24px'}
              >
                <TextInput
                  label={`${t('mix')} ${t('maxPlayers')}`}
                  id="mix_max_players"
                  name="mix_max_players"
                  type="number"
                  placeholder={'Pvz.: 8'}
                  required
                  handleInputChange={handleChange}
                  value={form.mix_max_players}
                />
              </Box>
            )}
          </>
        )}
        {form.isContinued === 1 && (
          <>
            <Box
              display={'flex'}
              flexDirection={'column'}
              maxWidth={{ xs: '100%', md: '100%' }}
              marginTop={'24px'}
            >
              <TextInput
                label={'Early bird nuolaidos kodas:'}
                id="earlyBirdCode"
                name="earlyBirdCode"
                type="text"
                placeholder={'Pvz.: ISLIK5'}
                required
                handleInputChange={handleChange}
                value={form.earlyBirdCode}
              />
            </Box>
            <Box
              display={'flex'}
              flexDirection={'column'}
              maxWidth={{ xs: '100%', md: '100%' }}
              marginTop={'24px'}
            >
              <Typography
                variant="body4"
                fontWeight={500}
                color={theme.palette.darkGrey[700]}
              >
                Early bird nuolaida iki:
              </Typography>
              <DatePicker
                slotProps={{
                  textField: {
                    size: 'small',
                    inputProps: {
                      style: {
                        fontSize: '16px',
                        color: theme.palette.darkGrey[500],
                        display: 'flex',
                      },
                    },
                    InputLabelProps: {
                      style: {
                        fontSize: '16px',
                        color: theme.palette.darkGrey[500],
                      },
                    },
                  },
                }}
                format="YYYY-MM-DD"
                onChange={(newValue) =>
                  setForm({
                    ...form,
                    earlyBirdDate: moment(newValue).format('YYYY-MM-DD'),
                  })
                }
                value={moment(form.earlyBirdDate)}
                sx={{
                  mt: '6px',
                }}
              />
            </Box>
          </>
        )}
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            Registracija iki:
          </Typography>
          <DatePicker
            slotProps={{
              textField: {
                size: 'small',
                inputProps: {
                  style: {
                    fontSize: '16px',
                    color: theme.palette.darkGrey[500],
                    display: 'flex',
                  },
                },
                InputLabelProps: {
                  style: {
                    fontSize: '16px',
                    color: theme.palette.darkGrey[500],
                  },
                },
              },
            }}
            format="YYYY-MM-DD"
            onChange={(newValue) =>
              setForm({
                ...form,
                registrationDate: moment(newValue).format('YYYY-MM-DD'),
              })
            }
            value={moment(form.registrationDate)}
            sx={{
              mt: '6px',
            }}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            Turnyro pradžios data:
          </Typography>
          <DatePicker
            slotProps={{
              textField: {
                size: 'small',
                inputProps: {
                  style: {
                    fontSize: '16px',
                    color: theme.palette.darkGrey[500],
                    display: 'flex',
                  },
                },
                InputLabelProps: {
                  style: {
                    fontSize: '16px',
                    color: theme.palette.darkGrey[500],
                  },
                },
              },
            }}
            format="YYYY-MM-DD"
            onChange={(newValue) =>
              setForm({
                ...form,
                tournamentStartDate: moment(newValue).format('YYYY-MM-DD'),
              })
            }
            value={moment(form.tournamentStartDate)}
            sx={{
              mt: '6px',
            }}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <TextInput
            label={'Turnyro pradžios diena:'}
            id="tournamentStartDay"
            name="tournamentStartDay"
            type="text"
            placeholder={'Pvz.: Kovo 1d.'}
            required
            handleInputChange={handleChange}
            value={form.tournamentStartDay}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            Finalų data:
          </Typography>
          <DatePicker
            slotProps={{
              textField: {
                size: 'small',
                inputProps: {
                  style: {
                    fontSize: '16px',
                    color: theme.palette.darkGrey[500],
                    display: 'flex',
                  },
                },
                InputLabelProps: {
                  style: {
                    fontSize: '16px',
                    color: theme.palette.darkGrey[500],
                  },
                },
              },
            }}
            format="YYYY-MM-DD"
            onChange={(newValue) =>
              setForm({
                ...form,
                finalsDate: moment(newValue).format('YYYY-MM-DD'),
              })
            }
            value={moment(form.finalsDate)}
            sx={{
              mt: '6px',
            }}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <TextInput
            label={'Turnyro finalinė diena:'}
            id="finalDay"
            name="finalDay"
            type="text"
            placeholder={'Pvz.: Kovo 1d.'}
            required
            handleInputChange={handleChange}
            value={form.finalDay}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            Grupių etapas iki:
          </Typography>
          <DatePicker
            slotProps={{
              textField: {
                size: 'small',
                inputProps: {
                  style: {
                    fontSize: '16px',
                    color: theme.palette.darkGrey[500],
                    display: 'flex',
                  },
                },
                InputLabelProps: {
                  style: {
                    fontSize: '16px',
                    color: theme.palette.darkGrey[500],
                  },
                },
              },
            }}
            format="YYYY-MM-DD"
            onChange={(newValue) =>
              setForm({
                ...form,
                groupsDate: moment(newValue).format('YYYY-MM-DD'),
              })
            }
            value={moment(form.groupsDate)}
            sx={{
              mt: '6px',
            }}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            Atkrintamųjų etapas iki:
          </Typography>
          <DatePicker
            slotProps={{
              textField: {
                size: 'small',
                inputProps: {
                  style: {
                    fontSize: '16px',
                    color: theme.palette.darkGrey[500],
                    display: 'flex',
                  },
                },
                InputLabelProps: {
                  style: {
                    fontSize: '16px',
                    color: theme.palette.darkGrey[500],
                  },
                },
              },
            }}
            format="YYYY-MM-DD"
            onChange={(newValue) =>
              setForm({
                ...form,
                eliminationsDate: moment(newValue).format('YYYY-MM-DD'),
              })
            }
            value={moment(form.eliminationsDate)}
            sx={{
              mt: '6px',
            }}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <TextInput
            label={'Turnyro mokestis:'}
            id="price"
            name="price"
            type="text"
            placeholder={'Pvz.: 40€'}
            required
            handleInputChange={handleChange}
            value={form.price}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <TextInput
            label={'Turnyro naujoko mokestis:'}
            id="new_customer_price"
            name="new_customer_price"
            type="text"
            placeholder={'Pvz.: 30€'}
            required
            handleInputChange={handleChange}
            value={form.new_customer_price}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <Typography
            variant="body4"
            fontWeight={500}
            color={theme.palette.darkGrey[700]}
          >
            {t('collectPayment')}
          </Typography>
          <SelectInput
            name="collect_payment"
            id="collect_payment"
            placeholder={t('collectPayment')}
            open={open}
            handleOpen={handleOpen}
            handleClose={handleClose}
            handleChange={handleChange}
            topMargin={'6px'}
            value={form.collect_payment}
            options={[
              {
                value: 1,
                label: 'Taip',
              },
              {
                value: 0,
                label: 'Ne',
              },
            ]}
          />
        </Box>
        <Box
          display={'flex'}
          flexDirection={'column'}
          maxWidth={{ xs: '100%', md: '100%' }}
          marginTop={'24px'}
        >
          <TextInput
            label={'Divizijos:'}
            id="divisions"
            name="divisions"
            type="text"
            placeholder={'Pvz.: 8'}
            required
            handleInputChange={handleChange}
            value={form.divisions}
          />
        </Box>
      </Box>
      <Box
        sx={{
          marginTop: '32px',
          display: 'flex',
          alignItems: 'center',
          justifyContent: 'flex-end',
        }}
      >
        <Button
          maxWidth={{ xs: '100%', md: '100%' }}
          width={'100%'}
          buttontype={'orange'}
          onClick={() => handleSubmit()}
          disabled={isSubmitting}
        >
          {isSubmitting ? <SpinningBox /> : 'Submit'}
        </Button>
      </Box>
    </Box>
  );
};

export default NewTournament;
